/** Teinte d’accent Mail — alignée sur la palette Coffre (12 couleurs). */

export const MAIL_ACCENT_STORAGE_KEY = "mail_accent";

export const MAIL_ACCENT_IDS = [
  "red",
  "orange",
  "yellow",
  "green",
  "blue",
  "purple",
  "pink",
  "slate",
  "teal",
  "indigo",
  "fuchsia",
  "lime",
] as const;

export type MailAccentId = (typeof MAIL_ACCENT_IDS)[number];

export type MailAccentPreset = {
  id: MailAccentId;
  label: string;
  swatch: string;
  accent: string;
  accentRgb: string;
  accentStrong: string;
  uiIcon: string;
  uiIconDark: string;
  gradientFrom: string;
  gradientTo: string;
};

export const MAIL_ACCENT_PRESETS: Record<MailAccentId, MailAccentPreset> = {
  red: {
    id: "red",
    label: "Rouge",
    swatch: "#dc2626",
    accent: "#dc2626",
    accentRgb: "220 38 38",
    accentStrong: "#b91c1c",
    uiIcon: "#b91c1c",
    uiIconDark: "#fca5a5",
    gradientFrom: "#fca5a5",
    gradientTo: "#dc2626",
  },
  orange: {
    id: "orange",
    label: "Orange",
    swatch: "#ea580c",
    accent: "#ea580c",
    accentRgb: "234 88 12",
    accentStrong: "#c2410c",
    uiIcon: "#c2410c",
    uiIconDark: "#fdba74",
    gradientFrom: "#fdba74",
    gradientTo: "#ea580c",
  },
  yellow: {
    id: "yellow",
    label: "Jaune",
    swatch: "#ca8a04",
    accent: "#ca8a04",
    accentRgb: "202 138 4",
    accentStrong: "#a16207",
    uiIcon: "#a16207",
    uiIconDark: "#fde047",
    gradientFrom: "#fde047",
    gradientTo: "#ca8a04",
  },
  green: {
    id: "green",
    label: "Vert",
    swatch: "#16a34a",
    accent: "#16a34a",
    accentRgb: "22 163 74",
    accentStrong: "#15803d",
    uiIcon: "#15803d",
    uiIconDark: "#86efac",
    gradientFrom: "#86efac",
    gradientTo: "#16a34a",
  },
  blue: {
    id: "blue",
    label: "Bleu Aurélia",
    swatch: "#71baff",
    accent: "#71baff",
    accentRgb: "113 186 255",
    accentStrong: "#2b7fd4",
    uiIcon: "#4338ca",
    uiIconDark: "#ce9bdd",
    gradientFrom: "#ce9bdd",
    gradientTo: "#71baff",
  },
  purple: {
    id: "purple",
    label: "Violet",
    swatch: "#7c3aed",
    accent: "#7c3aed",
    accentRgb: "124 58 237",
    accentStrong: "#6d28d9",
    uiIcon: "#6d28d9",
    uiIconDark: "#c4b5fd",
    gradientFrom: "#c4b5fd",
    gradientTo: "#7c3aed",
  },
  pink: {
    id: "pink",
    label: "Rose",
    swatch: "#db2777",
    accent: "#db2777",
    accentRgb: "219 39 119",
    accentStrong: "#be185d",
    uiIcon: "#be185d",
    uiIconDark: "#f9a8d4",
    gradientFrom: "#f9a8d4",
    gradientTo: "#db2777",
  },
  slate: {
    id: "slate",
    label: "Gris foncé",
    swatch: "#475569",
    accent: "#475569",
    accentRgb: "71 85 105",
    accentStrong: "#334155",
    uiIcon: "#334155",
    uiIconDark: "#94a3b8",
    gradientFrom: "#94a3b8",
    gradientTo: "#475569",
  },
  teal: {
    id: "teal",
    label: "Turquoise",
    swatch: "#0d9488",
    accent: "#0d9488",
    accentRgb: "13 148 136",
    accentStrong: "#0f766e",
    uiIcon: "#0f766e",
    uiIconDark: "#5eead4",
    gradientFrom: "#5eead4",
    gradientTo: "#0d9488",
  },
  indigo: {
    id: "indigo",
    label: "Indigo",
    swatch: "#4f46e5",
    accent: "#4f46e5",
    accentRgb: "79 70 229",
    accentStrong: "#4338ca",
    uiIcon: "#4338ca",
    uiIconDark: "#a5b4fc",
    gradientFrom: "#a5b4fc",
    gradientTo: "#4f46e5",
  },
  fuchsia: {
    id: "fuchsia",
    label: "Fuchsia",
    swatch: "#c026d3",
    accent: "#c026d3",
    accentRgb: "192 38 211",
    accentStrong: "#a21caf",
    uiIcon: "#a21caf",
    uiIconDark: "#e879f9",
    gradientFrom: "#e879f9",
    gradientTo: "#c026d3",
  },
  lime: {
    id: "lime",
    label: "Citron vert",
    swatch: "#84cc16",
    accent: "#84cc16",
    accentRgb: "132 204 22",
    accentStrong: "#65a30d",
    uiIcon: "#65a30d",
    uiIconDark: "#bef264",
    gradientFrom: "#bef264",
    gradientTo: "#84cc16",
  },
};

const MAIL_ACCENT_ID_SET = new Set<string>(MAIL_ACCENT_IDS);

export function normalizeMailAccentId(value: string | null | undefined): MailAccentId {
  if (value === "violet") return "purple";
  if (value && MAIL_ACCENT_ID_SET.has(value)) return value as MailAccentId;
  return "blue";
}

export function mailAccentPreset(accentId: MailAccentId | string | null | undefined): MailAccentPreset {
  return MAIL_ACCENT_PRESETS[normalizeMailAccentId(accentId ?? undefined)];
}

export function readMailAccent(): MailAccentId {
  try {
    return normalizeMailAccentId(localStorage.getItem(MAIL_ACCENT_STORAGE_KEY));
  } catch {
    return "blue";
  }
}

export function persistMailAccent(accentId: MailAccentId): void {
  try {
    localStorage.setItem(MAIL_ACCENT_STORAGE_KEY, normalizeMailAccentId(accentId));
  } catch {
    /* ignore */
  }
}
